import { cssMap } from '@atlaskit/css';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { JiraYourWorkMessage } from '@post-office/components--jira-your-work-message';
import { useMessageContext } from '@atlassian/post-office-context/message-context';
import { type ComponentProps, type FunctionComponent } from 'react';
// @ts-expect-error: 'React' is declared but its value is never read
import React from 'react';

import { LearnMoreButton } from './components/learn-more';
import { TryItNowButton } from './components/try-it-now';
import { ValueProposition } from './components/value-proposition';
import { type App } from './types';
import { type JSX } from 'react/jsx-runtime';

export type Props = {
	app: App;
	/** Should be used for hiding the message when dismissed. The messageDismissed event should not need to be fired using this. It will be fired internally within the message */
	onDismissed?: (messageInstanceId: string) => void;
	whyAmISeeingThisModalContent?: ComponentProps<
		typeof JiraYourWorkMessage
	>['whyAmISeeingThisModalContent'];
	extraAnalyticsAttributes?: Record<string, string>;
	iconColor?: NewCoreIconProps['color'];
};

type PropsWithApp = {
	app: App;
};

const styles = cssMap({
	bodyContent: {
		paddingInline: token('space.200'),
		gap: token('space.100'),
	},
	actionButton: {
		paddingLeft: token('space.200'),
		gap: token('space.050'),
	},
});

const DefaultActions = ({ app }: PropsWithApp) => {
	return (
		<>
			<TryItNowButton app={app} />
			<LearnMoreButton app={app} />
		</>
	);
};

const BodyContent: FunctionComponent<PropsWithApp> = ({ app }) => {
	return (
		<>
			<Stack xcss={styles.bodyContent}>
				{app.logo}
				<ValueProposition app={app} />
			</Stack>
			<Inline xcss={styles.actionButton}>{app.actions || <DefaultActions app={app} />}</Inline>
		</>
	);
};

export const JiraYourWorkFirstPartyRecommendation = ({
	app,
	onDismissed,
	whyAmISeeingThisModalContent,
	iconColor,
}: Props): JSX.Element => {
	const { messageInstanceId } = useMessageContext();
	if (!messageInstanceId) {
		throw new Error('messageInstanceId is required');
	}
	return (
		<JiraYourWorkMessage
			product={app.product}
			onDismissed={onDismissed}
			whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
			bodyContent={<BodyContent app={app} />}
			headerImageAltText={app.headerImageAltText}
			iconColor={iconColor}
		/>
	);
};
